import { createClient } from "@bezbolisno/contracts/client";
import { type PatientSession, type SelfView, telegram } from "@bezbolisno/contracts/telegram";
import { env } from "../env";

/** The Telegram account opened the app but is not bound to a patient yet (API: 404). */
export class NotLinkedError extends Error {
  constructor() {
    super("patient_not_linked");
    this.name = "NotLinkedError";
  }
}

/** The page is not running inside Telegram, so there is no signed payload to exchange. */
export class NoInitDataError extends Error {
  constructor() {
    super("no_init_data");
    this.name = "NoInitDataError";
  }
}

/** Any other refusal, carrying the problem's stable code (never its prose). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
}

export interface PatientApi {
  /** The current session, or null before the first exchange. */
  readonly session: PatientSession | null;
  /** Exchange `initData` for a patient token. */
  signIn(): Promise<PatientSession>;
  /** The patient's own view; refreshes the token when it is stale or rejected. */
  me(): Promise<SelfView>;
}

export interface PatientApiDeps {
  /** Raw Telegram `initData`; null outside Telegram. Injected so this module stays DOM-free. */
  readInitData: () => string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

/** Re-exchange this long before the token's own expiry, so a slow request cannot outrun it. */
const EXPIRY_SKEW_MS = 30_000;

const problemCode = (body: unknown): string =>
  typeof body === "object" && body !== null && typeof (body as { code?: unknown }).code === "string"
    ? (body as { code: string }).code
    : "unknown";

/**
 * The Mini App's whole data access. The token lives in memory only: it is valid for 15
 * minutes, and `initData` can always mint a new one, so persisting it would add risk
 * (a token in storage outlives the session) for nothing.
 */
export function createPatientApi(deps: PatientApiDeps): PatientApi {
  const now = deps.now ?? (() => Date.now());
  let session: PatientSession | null = null;

  const client = createClient(telegram, {
    baseUrl: deps.baseUrl ?? env.VITE_API_URL,
    fetch: deps.fetchImpl ?? fetch,
    // Bearer only: the patient tier refuses credentialed CORS, so cookies must never ride along.
    credentials: "omit",
    headers: (): Record<string, string> =>
      session ? { authorization: `Bearer ${session.token}` } : {},
  });

  async function signIn(): Promise<PatientSession> {
    const initData = deps.readInitData();
    if (!initData) throw new NoInitDataError();
    session = null;
    const res = await client.session({ body: { initData } });
    if (res.status === 200) {
      session = res.body;
      return session;
    }
    if (res.status === 404 && problemCode(res.body) === "patient_not_linked") {
      throw new NotLinkedError();
    }
    throw new ApiError(res.status, problemCode(res.body));
  }

  /** True when there is no token, or it is about to expire. */
  function stale(): boolean {
    if (!session) return true;
    const expiresAt = Date.parse(session.expiresAt);
    return Number.isNaN(expiresAt) ? false : expiresAt - now() <= EXPIRY_SKEW_MS;
  }

  async function me(): Promise<SelfView> {
    if (stale()) await signIn();
    let res = await client.me({});
    if (res.status === 401) {
      // The token was rejected (clock skew, a restarted API): mint one more and retry once.
      await signIn();
      res = await client.me({});
    }
    if (res.status === 200) return res.body;
    throw new ApiError(res.status, problemCode(res.body));
  }

  return {
    get session() {
      return session;
    },
    signIn,
    me,
  };
}
